export enum ActivityLogType {
    CREATE = 'CREATE',
    UPDATE = 'UPDATE',
    DELETE = 'DELETE',
    ARCHIVE = 'ARCHIVE',
}

export const ActivityLogTypeName: Record<ActivityLogType, string> = {
    [ActivityLogType.CREATE]: 'created',
    [ActivityLogType.UPDATE]: 'updated',
    [ActivityLogType.DELETE]: 'deleted',
    [ActivityLogType.ARCHIVE]: 'archived',
};

export enum ActivityLogModelType {
    FORMULA = 'Formula',
    LABEL = 'Label',
    FORMULA_LABEL = 'FormulaLabel',
    ALTERNATE = 'AlternateText',
    VARIANT = 'Variant',
    LABEL_VARIANT_TYPE = 'LabelVariantType',
    FORMULA_AUDIENCE = 'FormulaAudience',
    AUDIENCE = 'Audience',
    FORMULA_TAG = 'FormulaTag',
    TAG = 'Tag',
    COMPOUND_TRIGGER = 'CompoundTrigger',
    FREQUENCY_LIMIT = 'FrequencyLimit',
    DEFAULT_FREQUENCY_LIMITS = 'DefaultFrequencyLimits',
    REWARD_FUNCTION = 'RewardFunction',
    TARGET_EVENT = 'TargetEvent',
    REWARD_EVENT_WEIGHT_OVERRIDE = 'RewardEventWeightOverride',
    MESSAGE_CONTENT = 'MessageContent',
}

export const ActivityLogModelName: Record<ActivityLogModelType, string> = {
    [ActivityLogModelType.FORMULA]: 'Message',
    [ActivityLogModelType.LABEL]: 'Label',
    [ActivityLogModelType.FORMULA_LABEL]: 'Message label',
    [ActivityLogModelType.ALTERNATE]: 'Alternate text',
    [ActivityLogModelType.VARIANT]: 'Variant',
    [ActivityLogModelType.LABEL_VARIANT_TYPE]: 'Label variant',
    [ActivityLogModelType.FORMULA_AUDIENCE]: 'Message audience',
    [ActivityLogModelType.AUDIENCE]: 'Audience',
    [ActivityLogModelType.FORMULA_TAG]: 'Message tag',
    [ActivityLogModelType.TAG]: 'Tag',
    [ActivityLogModelType.COMPOUND_TRIGGER]: 'Trigger',
    [ActivityLogModelType.FREQUENCY_LIMIT]: 'Frequency limit',
    // A per-trigger cap; not what the "Default frequency limits" page saves (those are FrequencyLimit rows).
    [ActivityLogModelType.DEFAULT_FREQUENCY_LIMITS]: 'Trigger message cap',
    [ActivityLogModelType.REWARD_FUNCTION]: 'Reward function',
    [ActivityLogModelType.TARGET_EVENT]: 'Reward target event',
    [ActivityLogModelType.MESSAGE_CONTENT]: 'Message content',
    [ActivityLogModelType.REWARD_EVENT_WEIGHT_OVERRIDE]: 'Event reward weight',
};

export const getModelName = (type: string | null | undefined): string => {
    if (!type) return 'Item';
    return ActivityLogModelName[type as ActivityLogModelType] ?? type;
};

export interface ActivityLogChange {
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    changed_fields: string[];
    /** Message entries: the variants, content etc. that changed in the same run. */
    parts?: ActivityLogPart[];
}

/** One changed part of a message (composer-backend !2061). */
export interface ActivityLogPart {
    object_type: string;
    object_id: string | number;
    verb: ActivityLogType | string;
    activity_id: number;
    first_activity_id: number;
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    changed_fields: string[];
}

/** GET /activity-logs/{id}/fields/{field}: the full value behind a sha256: digest. */
export interface ActivityLogFieldValue {
    field: string;
    before: { value: unknown; available: boolean };
    after: { value: unknown; available: boolean };
}

/** track_changes payloads always carry a changed_fields array; legacy snapshots never do. */
export const isActivityLogChange = (data: unknown): data is ActivityLogChange =>
    Array.isArray(
        (data as ActivityLogChange | null | undefined)?.changed_fields,
    );

export enum AuditTrailTimeframeOptions {
    LIFETIME = 'lifetime',
    MONTH = 'month',
    WEEK = 'week',
}

export interface ActivtyLog {
    id: number;
    user_id: number;
    user_name: string | null;
    verb: ActivityLogType;
    /** null / flat snapshot (legacy @log_activity) or ActivityLogChange (track_changes). */
    data?: unknown;
    transaction_id?: number;
    object_type: ActivityLogModelType | string | null;
    object_id?: string | number | null;
    target_type?: ActivityLogModelType | string | null;
    target_id?: string | number | null;
    /**
     * Consecutive saves of one object by one user are merged into one entry
     * (composer-backend !2060). For merged entries id/created_at are the last
     * save's; first_* describe the first.
     */
    save_count?: number;
    first_activity_id?: number;
    first_created_at?: string;
    /** Display name of object_id, resolved by the backend when it can (optional). */
    object_name?: string | null;
    /** Display name of target_id, resolved by the backend when it can (optional). */
    target_name?: string | null;
    created_at: string;
    /** Set to true when this update approved/set the formula live. NOT currently returned by /activity-logs — backend follow-up needed to expose this field. */
    approved?: boolean;
    /** Formula name injected client-side by useSurfaceActivityLogsQuery. */
    formula_name?: string;
}

export interface AuditTrailFilterValue {
    timeframe: AuditTrailTimeframeOptions;
    person: string;
}

export const DEFAULT_AUDIT_TRAIL_FILTERS: AuditTrailFilterValue = {
    timeframe: AuditTrailTimeframeOptions.LIFETIME,
    person: 'all',
};

/** GET /activity-logs/account/users: everyone who has written activity in the account. */
export interface ActivityLogAuthor {
    user_id: number;
    user_name: string | null;
    email: string | null;
}

/** Query params for GET /activity-logs/account (composer-backend !2057). `end` is exclusive. */
export interface AccountActivityLogFilters {
    start?: string;
    end?: string;
    object_type?: ActivityLogModelType;
    /** Rows of this type or filed under it (composer-backend !2063). */
    scope?: ActivityLogModelType;
    user_id?: number;
    target_model?: ActivityLogModelType;
    target_id?: string;
    surface_id?: string;
    page?: number;
    per_page?: number;
}

export interface ActivityLogFilters {
    target_id: string;
    target_model: string;
}
